import type { FC, ReactNode } from 'react'

import { Mail, Phone, Printer } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { BrandRule } from '@/components/generals/brand-rule'
import { PageHero } from '@/components/pages/page-hero'
import { ContactForm } from '@/components/pages/contact-form'
import { LocationMap } from '@/components/pages/location-map'
import { Reveal } from '@/components/motion/reveal'
import { contact } from '@/constants/site'

const KontaktPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Kontakt'
                description='Kontakt zu Wirtschaftsprüfer und Steuerberater Markus Balle in Stuttgart-Vaihingen: Telefon 0711 78 78 530, E-Mail info@markus-balle.de oder direkt über das Kontaktformular.'
            />

            <PageHero
                breadcrumbs={[
                    { label: 'Startseite', href: '/' },
                    { label: 'Kontakt' }
                ]}
                eyebrow='Kontakt'
                title='Interesse *geweckt?*'
                lead='Zögern Sie nicht, mich zu kontaktieren. Gerne kläre ich mit Ihnen in einem persönlichen, unverbindlichen Gespräch, ob ich Ihren Beratungsbedarf abdecken kann.'
            />

            <section aria-label='Kontaktmöglichkeiten' className='pb-28 pt-16 lg:pb-40 lg:pt-24'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                    <div className='space-y-6 lg:col-span-5'>
                        <Reveal>
                            <div className='rounded-[1.75rem] bg-graphite p-8 text-paper sm:p-10'>
                                <p className='font-mono text-[0.68rem] uppercase tracking-[0.18em] text-paper/60'>
                                    Kanzlei
                                </p>
                                <p className='mt-5 font-heading text-[2.25rem] leading-[1.02]'>
                                    {contact.fullName}
                                </p>
                                <p className='mt-2 text-paper/65'>
                                    {contact.titles}
                                </p>

                                <address className='mt-8 not-italic leading-relaxed text-paper/85'>
                                    {contact.street}
                                    <br />
                                    D-{contact.zip} {contact.city}
                                </address>

                                <BrandRule className='my-8 max-w-48' />

                                <ul className='space-y-4'>
                                    <li>
                                        <a href={contact.phone.href} className='group flex items-center gap-4'>
                                            <span className='grid size-11 shrink-0 place-items-center rounded-full border border-paper/20 transition-colors duration-500 group-hover:border-brand group-hover:bg-brand group-hover:text-ink'>
                                                <Phone aria-hidden='true' className='size-4' strokeWidth={1.75} />
                                            </span>
                                            <span>
                                                <span className='block text-xs text-paper/55'>Telefon</span>
                                                <span className='tabular link-grow font-heading text-[1.7rem] leading-tight'>{contact.phone.international}</span>
                                            </span>
                                        </a>
                                    </li>
                                    <li className='flex items-center gap-4'>
                                        <span className='grid size-11 shrink-0 place-items-center rounded-full border border-paper/20'>
                                            <Printer aria-hidden='true' className='size-4' strokeWidth={1.75} />
                                        </span>
                                        <span>
                                            <span className='block text-xs text-paper/55'>Telefax</span>
                                            <span className='tabular text-paper/85'>{contact.fax.international}</span>
                                        </span>
                                    </li>
                                    <li>
                                        <a href={`mailto:${contact.email}`} className='group flex items-center gap-4'>
                                            <span className='grid size-11 shrink-0 place-items-center rounded-full border border-paper/20 transition-colors duration-500 group-hover:border-brand group-hover:bg-brand group-hover:text-ink'>
                                                <Mail aria-hidden='true' className='size-4' strokeWidth={1.75} />
                                            </span>
                                            <span>
                                                <span className='block text-xs text-paper/55'>E-Mail</span>
                                                <span className='link-grow text-paper'>{contact.email}</span>
                                            </span>
                                        </a>
                                    </li>
                                </ul>

                                <p className='mt-8 border-t border-rule-dark pt-6 text-sm leading-relaxed text-paper/60'>
                                    Termine nach Vereinbarung, ohne starre Büroöffnungszeiten. Dank moderner Technologie auch gerne digital.
                                </p>
                            </div>
                        </Reveal>

                        <Reveal delay={0.1}>
                            <LocationMap />
                        </Reveal>
                    </div>

                    <div className='lg:col-span-7'>
                        <Reveal delay={0.05}>
                            <h2 className='mb-8 font-heading text-[clamp(2.2rem,3.6vw,3.25rem)] leading-[1] text-ink'>
                                Schreiben Sie mir.
                            </h2>
                            <ContactForm />
                        </Reveal>
                    </div>
                </Container>
            </section>
        </Layout>
    )
}

export default KontaktPage
